/**
 * SportsPicker — typeahead + chips. Type to filter the canonical sport list;
 * picked sports appear as removable chips above the input. Typing something
 * that doesn't match any canonical slug shows an "Add as Other: <text>" row
 * that captures it via the free-text overflow field.
 *
 * Backend contract is unchanged (slug array + optional sports_other string)
 * so this works as a drop-in for the lead modal, the profile wizard, and the
 * announcement audience filter.
 *
 * Props:
 *   value         — string[] of selected slugs
 *   other         — free-text string for "other" (ignored when allowOther=false)
 *   onChange      — (sports, sports_other) => void
 *   testidPrefix  — base for data-testid hooks
 *   allowOther    — boolean, default true. Set false on the audience filter
 *                   (we can't pre-segment by free-text values).
 *   placeholder   — input placeholder text
 */
import { useState, useRef, useEffect, useMemo } from "react";
import { X, Search } from "lucide-react";
import { SPORT_GROUPS, SPORT_LABELS } from "@/lib/athleteTaxonomy";

const FLAT_SPORTS = SPORT_GROUPS.flatMap((g) =>
    g.sports.map((s) => ({ ...s, group: g.group }))
).filter((s) => s.value !== "other");

export default function SportsPicker({
    value = [],
    other = "",
    onChange,
    testidPrefix = "sports",
    allowOther = true,
    placeholder = "Type a sport… (e.g., softball)",
}) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const wrapRef = useRef(null);

    const selected = useMemo(() => new Set(value), [value]);

    // Close dropdown on outside click.
    useEffect(() => {
        const onDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    const q = query.trim().toLowerCase();
    const matches = useMemo(() => {
        if (!q) {
            // Empty query: show the full list (minus already-picked) so users
            // can still browse on first focus.
            return FLAT_SPORTS.filter((s) => !selected.has(s.value)).slice(0, 50);
        }
        return FLAT_SPORTS.filter(
            (s) =>
                !selected.has(s.value) &&
                (s.label.toLowerCase().includes(q) || s.value.includes(q))
        ).slice(0, 30);
    }, [q, selected]);

    // "Add as Other" row when we've typed something that doesn't match anything
    // canonical AND free-text is allowed.
    const showOtherRow = allowOther && q.length > 0 && matches.length === 0;

    useEffect(() => {
        // Reset highlight when the list changes.
        setActiveIndex(0);
    }, [q, matches.length]);

    const addSlug = (slug) => {
        if (selected.has(slug)) return;
        const next = [...value, slug];
        onChange(next, other);
        setQuery("");
        setOpen(true);
    };

    const addOther = () => {
        const text = query.trim();
        if (!text) return;
        const next = selected.has("other") ? [...value] : [...value, "other"];
        onChange(next, other ? `${other}, ${text}` : text);
        setQuery("");
        setOpen(true);
    };

    const removeSlug = (slug) => {
        const next = value.filter((s) => s !== slug);
        // Dropping "other" also clears the free-text overflow.
        const nextOther = slug === "other" ? "" : other;
        onChange(next, nextOther);
    };

    const onKeyDown = (e) => {
        if (!open) setOpen(true);
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, matches.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (matches.length > 0) addSlug(matches[activeIndex].value);
            else if (showOtherRow) addOther();
        } else if (e.key === "Escape") {
            setOpen(false);
        } else if (e.key === "Backspace" && query === "" && value.length > 0) {
            // Pop the last chip when backspacing on an empty input.
            removeSlug(value[value.length - 1]);
        }
    };

    return (
        <div ref={wrapRef} data-testid={`${testidPrefix}-picker`} className="relative">
            {/* Selected chips */}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {value.map((slug) => (
                        <span
                            key={slug}
                            data-testid={`${testidPrefix}-chip-${slug}`}
                            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-brand-red bg-brand-red text-white"
                        >
                            {slug === "other" && other ? `Other: ${other}` : SPORT_LABELS[slug] || slug}
                            <button
                                type="button"
                                aria-label={`Remove ${slug}`}
                                onClick={() => removeSlug(slug)}
                                className="hover:text-zinc-200"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Input */}
            <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input
                    type="text"
                    data-testid={`${testidPrefix}-input`}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-brand-red"
                />
            </div>

            {/* Dropdown */}
            {open && (matches.length > 0 || showOtherRow) && (
                <ul
                    role="listbox"
                    data-testid={`${testidPrefix}-dropdown`}
                    className="absolute z-30 mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-zinc-950 border border-zinc-700 rounded-lg shadow-xl"
                >
                    {matches.map((m, i) => (
                        <li
                            key={m.value}
                            role="option"
                            aria-selected={i === activeIndex}
                            data-testid={`${testidPrefix}-option-${m.value}`}
                            onMouseDown={(e) => { e.preventDefault(); addSlug(m.value); }}
                            onMouseEnter={() => setActiveIndex(i)}
                            className={`px-3 py-2 text-sm flex items-center justify-between cursor-pointer ${
                                i === activeIndex ? "bg-brand-red/20 text-white" : "text-zinc-200 hover:bg-zinc-900"
                            }`}
                        >
                            <span>{m.label}</span>
                            <span className="text-[10px] tracking-wider uppercase text-zinc-500">{m.group}</span>
                        </li>
                    ))}
                    {showOtherRow && (
                        <li
                            role="option"
                            data-testid={`${testidPrefix}-option-other`}
                            onMouseDown={(e) => { e.preventDefault(); addOther(); }}
                            className="px-3 py-2 text-sm flex items-center gap-2 cursor-pointer bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border-t border-zinc-800"
                        >
                            <span className="text-brand-red">＋</span>
                            Add as Other: <span className="text-white font-semibold">"{query.trim()}"</span>
                        </li>
                    )}
                </ul>
            )}

            {!allowOther && other && (
                // Display-only freeform overflow (audience filter case won't reach this).
                <p className="text-[11px] text-zinc-400 mt-1">Other: {other}</p>
            )}
        </div>
    );
}
